import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleCheck, Sparkles } from "lucide-react";
import { PLATFORMS, type Platform } from "@metrickle/schema/constants";
import { INSTALL_METHOD_LABEL, INSTALL_METHOD_LANGUAGE, installSnippets, type InstallMethod } from "@metrickle/schema/install";
import { authClient } from "../lib/auth";
import { api, useApiMutation, useApp, type App, type EventRecord } from "../lib/api";
import { useOrgAccess, useOrgCan } from "../lib/access";
import { PLATFORM_LABEL } from "../lib/format";
import { usePageTitle } from "../lib/page-title";
import { track } from "../lib/track";
import { forgetInvite, redeemInvite, rememberedInvite, useBillingMode } from "../lib/early-access";
import { parseDomains, slugify, suggestWorkspaceName, workDomain } from "../lib/onboarding";
import { OnboardingFrame, OnboardingPreview, PLATFORM_ICON } from "../components/onboarding-frame";
import { Button, ButtonLink, Callout, CodeBlock, Disclosure, ErrorState, Field, FieldError, Input, RadioCards, Skeleton, TabBar, cx, notifyError } from "../components/ui";

/** Small counts in words, as the plans page writes them ("three apps"). */
const words = (n: number) => ["no", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"][n] ?? String(n);

// --- Step 1: workspace ----------------------------------------------------------------------

/** Creates a workspace (company, client or team), then moves on to adding its first app. */
export function Onboarding() {
  const [params] = useSearchParams();
  const isAdditional = params.get("new") === "1";
  usePageTitle(isAdditional ? "Add another workspace" : "Name your workspace");
  const session = authClient.useSession();
  const user = session.data?.user;
  const orgs = authClient.useListOrganizations();
  const active = authClient.useActiveOrganization();
  const navigate = useNavigate();
  const qc = useQueryClient();
  // apps/api/src/auth.ts FREE_WORKSPACE_LIMIT: Free is one workspace of your own. Say so up front.
  const freeOwned = useQuery({
    queryKey: ["free-workspace"],
    queryFn: () => api<{ workspace: { id: string; name: string; appLimit: number; appsLeft: number } | null }>("/me/free-workspace"),
    enabled: isAdditional,
  });
  // If the check fails, show the form: creating still refuses with FREE_WORKSPACE_LIMIT.
  const atLimit = isAdditional ? freeOwned.data?.workspace ?? null : null;
  const checking = isAdditional && freeOwned.isPending;
  const billingOpen = useBillingMode().data?.open ?? false;
  const [opening, setOpening] = useState<"billing" | "app" | null>(null);
  /** Opens a page in the Free workspace, which may not be the one that's active. */
  const openIn = async (workspaceId: string, to: string, which: "billing" | "app") => {
    setOpening(which);
    try {
      if (active.data?.id !== workspaceId) {
        await authClient.organization.setActive({ organizationId: workspaceId });
        await qc.invalidateQueries();
      }
      track("free_limit_choice", { choice: which });
      navigate(to);
    } catch (err) {
      setOpening(null);
      notifyError(err);
    }
  };

  const [name, setName] = useState("");
  const [touched, setTouched] = useState(false);
  const suggested = isAdditional ? "" : suggestWorkspaceName(user?.email, user?.name);
  useEffect(() => {
    if (!touched && suggested) setName(suggested);
  }, [suggested, touched]);

  const [error, setError] = useState<string | null>(null);
  const [freeLimit, setFreeLimit] = useState(false);
  const [pending, setPending] = useState(false);
  const [createdId, setCreatedId] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Enter a name for the workspace.");
      return;
    }
    setPending(true);
    setError(null);
    setFreeLimit(false);
    // Arrived from an invite link: use the code first, so the workspace starts on Growth. A code
    // that doesn't work leaves it on Free, and stays waiting on the billing page to try again.
    const invite = rememberedInvite();
    if (invite) {
      const used = await redeemInvite(invite).then(
        () => true,
        () => false,
      );
      if (used) {
        forgetInvite();
        track("early_access_code_redeemed");
      }
    }
    const trimmed = name.trim();
    const suffix = crypto.getRandomValues(new Uint32Array(1))[0]!.toString(36).slice(0, 4);
    const res = await authClient.organization.create({ name: trimmed, slug: `${slugify(trimmed)}-${suffix}` });
    if (res.error || !res.data) {
      setPending(false);
      setError(res.error?.message ?? "Couldn't create the workspace");
      setFreeLimit(res.error?.code === "FREE_WORKSPACE_LIMIT");
      return;
    }
    track("workspace_created", { additional: isAdditional, suggested: !!suggested && trimmed === suggested });
    await authClient.organization.setActive({ organizationId: res.data.id });
    await qc.invalidateQueries();
    // RequireAuth reads better-auth's own organization list, which React Query doesn't refresh.
    // Navigating before it has the new workspace bounces straight back here, so wait for it.
    setCreatedId(res.data.id);
    orgs.refetch();
  };

  useEffect(() => {
    if (createdId && orgs.data?.some((o) => o.id === createdId)) navigate("/onboarding/app", { replace: true });
  }, [createdId, orgs.data, navigate]);

  const other = isAdditional ? active.data?.name : undefined;
  const fromEmail = !isAdditional && !touched && !!suggested && name === suggested;

  return (
    <OnboardingFrame
      step={atLimit ? undefined : 1}
      title={isAdditional ? "Add another workspace" : "Name your workspace"}
      subtitle={
        isAdditional
          ? `Use a separate workspace for a client or a team whose apps and people stay apart${other ? ` from ${other}` : ""}.`
          : "Usually your company or team. Your apps and teammates live here, and you can rename it any time."
      }
      preview={
        atLimit ? (
          <OnboardingPreview
            step={2}
            current={atLimit.appsLeft > 0 ? "Add it here instead" : "All of Free's apps are in use"}
            eyebrow={atLimit.appsLeft > 0 ? "Or keep it together" : "Free's limits"}
            headline={
              atLimit.appsLeft > 0 ? `One workspace can hold several apps. Add your next one to ${atLimit.name}.` : `${atLimit.name} is using all ${words(atLimit.appLimit)} of Free's apps.`
            }
            workspace={atLimit.name}
          />
        ) : (
          <OnboardingPreview
            step={1}
            eyebrow="Your workspace"
            headline={isAdditional ? "Keep each client's apps and people apart." : "One place for every app your team ships, and every release we judge."}
            workspace={name.trim()}
            otherWorkspace={other}
          />
        )
      }
    >
      {checking ? (
        <div aria-busy className="space-y-3">
          <p role="status" className="sr-only">
            Checking your plan…
          </p>
          <Skeleton className="h-16" />
          <Skeleton className="h-11" />
        </div>
      ) : atLimit ? (
        <div className="space-y-5">
          <Callout tone="warn" title={`Free includes one workspace of your own, and ${atLimit.name} is using it.`}>
            Workspaces you've joined but don't own don't count. To add another,{" "}
            {billingOpen ? `move ${atLimit.name} to Growth.` : `ask for Growth for ${atLimit.name}, free while we launch.`}{" "}
            {atLimit.appsLeft > 0
              ? `Or add your next app to ${atLimit.name}: Free holds up to ${words(atLimit.appLimit)} apps, and you choose who sees each one.`
              : `Growth also lifts its limit of ${words(atLimit.appLimit)} apps.`}
          </Callout>
          <div className="flex flex-col gap-2">
            <Button variant="primary" className="w-full" pending={opening === "billing"} pendingLabel="Opening…" disabled={!!opening} onClick={() => void openIn(atLimit.id, "/workspace/billing", "billing")}>
              {billingOpen ? `Move ${atLimit.name} to Growth` : "Ask for Growth"}
            </Button>
            {atLimit.appsLeft > 0 && (
              <Button className="w-full" pending={opening === "app"} pendingLabel="Opening…" disabled={!!opening} onClick={() => void openIn(atLimit.id, "/apps/new", "app")}>
                Add an app to {atLimit.name} instead
              </Button>
            )}
            <Button type="button" variant="link" className="self-center" onClick={() => navigate(-1)}>
              Cancel and go back{other ? ` to ${other}` : ""}
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} noValidate className="space-y-5">
          <Field
            label="Workspace name"
            hint={
              fromEmail ? (
                <span className="inline-flex items-start gap-1.5">
                  <Sparkles size={13} className="mt-0.5 shrink-0" />
                  {workDomain(user?.email) ? "Filled in from your email address. Change it if your team goes by another name." : "A starting name. Your company's name works best if you'll invite teammates."}
                </span>
              ) : isAdditional ? (
                "For example, a client's name. People only see the workspaces they're in."
              ) : (
                "You can rename it any time in Workspace settings."
              )
            }
            error={
              error && (
                <>
                  {error}
                  {freeLimit && (
                    <>
                      {" "}
                      <Link className="underline" to="/workspace/billing">
                        Open Plan &amp; billing
                      </Link>
                      .
                    </>
                  )}
                </>
              )
            }
            alert
          >
            <Input
              autoComplete="organization"
              maxLength={60}
              placeholder={isAdditional ? "Client or team name" : "Company or team name"}
              value={name}
              onChange={(e) => {
                setTouched(true);
                setName(e.target.value);
                if (error && e.target.value.trim()) setError(null);
              }}
            />
          </Field>
          <div className="flex flex-col gap-2">
            <Button variant="primary" className="w-full" pending={pending} pendingLabel="Creating…">
              Create workspace
            </Button>
            {isAdditional ? (
              <Button type="button" variant="link" className="self-center" onClick={() => navigate(-1)}>
                Cancel and go back{other ? ` to ${other}` : ""}
              </Button>
            ) : (
              <p className="text-center text-[13px] text-ink-3">About 2 minutes to your first data.</p>
            )}
          </div>
        </form>
      )}
    </OnboardingFrame>
  );
}

// --- Step 2: app ----------------------------------------------------------------------------

const PLATFORM_HINT: Record<Platform, string> = {
  web: "A website or web app",
  ios: "Swift, UIKit or SwiftUI",
  android: "Kotlin, Views or Compose",
  "react-native": "One app for iOS and Android",
  flutter: "One app for iOS and Android",
  server: "Any backend, by HTTP",
};
const NAME_SUFFIX: Record<Platform, string> = {
  web: "web app",
  ios: "for iOS",
  android: "for Android",
  "react-native": "app",
  flutter: "app",
  server: "backend",
};

export interface AppDraft {
  name: string;
  platform: Platform;
  domains: string;
}

/**
 * The fields for a new app: where it runs, its name and (for the web) the sites that may send
 * data. The name follows the platform until someone types their own. Shared by set-up and /apps/new.
 */
export function AppFields({ workspace, email, value, onChange, nameError }: { workspace: string; email?: string; value: AppDraft; onChange: (v: AppDraft) => void; nameError?: string | null }) {
  const [nameEdited, setNameEdited] = useState(false);
  const domain = workDomain(email);
  return (
    <div className="space-y-5">
      <RadioCards
        label="Where does it run?"
        name="platform"
        columns={2}
        items={PLATFORMS.map((p) => {
          const Icon = PLATFORM_ICON[p];
          return { value: p, label: PLATFORM_LABEL[p] === "Web" ? "Website" : PLATFORM_LABEL[p] ?? p, hint: PLATFORM_HINT[p], icon: <Icon size={16} /> };
        })}
        value={value.platform}
        onValueChange={(p) => {
          const platform = p as Platform;
          onChange({ ...value, platform, name: nameEdited ? value.name : `${workspace} ${NAME_SUFFIX[platform]}`.trim() });
        }}
      />
      <Field label="App name" hint="What your team calls it. It shows in reports and alerts." error={nameError} alert>
        <Input
          required
          maxLength={80}
          value={value.name}
          onChange={(e) => {
            setNameEdited(e.target.value.trim() !== "");
            onChange({ ...value, name: e.target.value });
          }}
        />
      </Field>
      {value.platform === "web" && (
        <Field
          label="Your website's address"
          hint={
            value.domains.trim()
              ? "Only these sites and their subdomains can send data, so nobody can copy your code onto another site. Separate several with commas."
              : "Leave it empty and any site can send data with your code. You can add addresses later."
          }
        >
          <Input inputMode="url" spellCheck={false} placeholder={`${domain ?? "example.com"}, staging.${domain ?? "example.com"}`} value={value.domains} onChange={(e) => onChange({ ...value, domains: e.target.value })} />
        </Field>
      )}
    </div>
  );
}

/** Create-app mutation for the active workspace, shared by set-up and /apps/new. */
export function useCreateApp(orgId: string | undefined) {
  return useApiMutation(
    (body: { name: string; platform: Platform; domains: string[]; timezone: string }) => api<App>(`/orgs/${orgId}/apps`, { method: "POST", json: body }),
    [["apps"], ["portfolio"], ["workspaces"]],
  );
}

export function OnboardingApp() {
  usePageTitle("Add the app you want to measure");
  const active = authClient.useActiveOrganization();
  const session = authClient.useSession();
  const email = session.data?.user.email;
  const navigate = useNavigate();
  const orgId = active.data?.id;
  const workspace = active.data?.name ?? "";
  const access = useOrgAccess(orgId);
  const canAdd = useOrgCan(orgId)("apps.create");
  const create = useCreateApp(orgId);
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [draft, setDraft] = useState<AppDraft | null>(null);
  // Fill in once the workspace has loaded: "Quillmere web app" on quillmere.example.
  useEffect(() => {
    if (!draft && workspace) setDraft({ platform: "web", name: `${workspace} ${NAME_SUFFIX.web}`, domains: workDomain(email) ?? "" });
  }, [draft, workspace, email]);
  const [nameError, setNameError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!draft) return;
    if (!draft.name.trim()) {
      setNameError("Enter a name for the app.");
      return;
    }
    try {
      const app = await create.mutateAsync({ name: draft.name.trim(), platform: draft.platform, domains: parseDomains(draft.domains), timezone });
      track("app_created", { platform: draft.platform, domains: app.domains.length, onboarding: true });
      navigate(`/onboarding/install/${app.id}?setup=1`);
    } catch (err) {
      notifyError(err);
    }
  };

  return (
    <OnboardingFrame
      step={2}
      eyebrow={workspace ? `${workspace} is ready` : undefined}
      title="Add the app you want to measure"
      subtitle="Start with the one where people sign up or pay. You can add your other apps afterwards."
      preview={
        <OnboardingPreview
          step={2}
          eyebrow="Your app"
          headline="Start where people sign up or pay. That's where a bad release costs the most."
          workspace={workspace}
          app={draft ? { name: draft.name.trim(), platform: draft.platform } : undefined}
        />
      }
    >
      {active.isPending || !draft ? (
        <div aria-busy className="space-y-3">
          <Skeleton className="h-48" />
          <Skeleton className="h-11" />
        </div>
      ) : access.data && !canAdd ? (
        <Callout tone="info" title="You can't add apps in this workspace">
          Ask a workspace admin to add one, or for permission to add apps. <Link to="/">Go to {workspace}</Link>
        </Callout>
      ) : (
        <form onSubmit={submit} noValidate className="space-y-5">
          <AppFields
            workspace={workspace}
            email={email}
            value={draft}
            onChange={(v) => {
              setDraft(v);
              if (v.name.trim()) setNameError(null);
            }}
            nameError={nameError}
          />
          <p className="text-[13px] text-ink-3">
            Reports use your time zone, <span className="font-medium text-ink-2">{timezone}</span>. You can change it in the app's settings.
          </p>
          <div className="flex flex-col gap-2">
            <Button variant="primary" className="w-full" pending={create.isPending} pendingLabel="Adding…" disabled={!orgId}>
              Add app
            </Button>
            <p className="text-center text-[13px] text-ink-3">Next: the code to install, and a live check that it works.</p>
          </div>
        </form>
      )}
    </OnboardingFrame>
  );
}

// --- Step 3: install ------------------------------------------------------------------------

const METHODS: Record<Platform, InstallMethod[]> = {
  web: ["script", "npm"],
  ios: ["ios"],
  android: ["android"],
  "react-native": ["reactNative"],
  flutter: ["flutter"],
  server: ["http"],
};
/** "14:32": polling stops once the event is in, so a relative time would go stale. */
const fmtClock = (ts: number) => new Date(ts).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
/** How often the live check asks for the first event. */
const POLL_MS = 3000;
/** After this long with nothing, open the troubleshooting list. */
const HELP_AFTER_MS = 45_000;

/** The app's newest event, polled until there is one. */
function useFirstEvent(appId: string | undefined) {
  return useQuery({
    queryKey: ["first-event", appId],
    queryFn: () => api<EventRecord[]>(`/apps/${appId}/events?limit=1`).then((rows) => rows[0] ?? null),
    enabled: !!appId,
    refetchInterval: (q) => (q.state.data ? false : POLL_MS),
    refetchIntervalInBackground: true,
  });
}

/** The install code for a new app, then a live check that waits for its first event. */
export function OnboardingInstall() {
  const { appId } = useParams();
  const [params] = useSearchParams();
  const setup = params.get("setup") === "1";
  const app = useApp(appId);
  const active = authClient.useActiveOrganization();
  const workspace = active.data?.name ?? "";
  const first = useFirstEvent(appId);
  const event = first.data ?? null;
  const [method, setMethod] = useState<InstallMethod | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    if (event) return;
    const t = setTimeout(() => setHelpOpen(true), HELP_AFTER_MS);
    return () => clearTimeout(t);
  }, [event]);
  const goRef = useRef<HTMLAnchorElement>(null);
  useEffect(() => {
    if (!event) return;
    track("install_first_event_seen", { platform: app.data?.platform ?? "unknown", setup });
    // "I'll install later" and "Nothing showing up?" go away on arrival: don't drop focus to the page.
    const focused = document.activeElement;
    if (!focused || focused === document.body || !focused.isConnected) goRef.current?.focus();
  }, [!!event]); // eslint-disable-line react-hooks/exhaustive-deps

  const a = app.data;
  const web = a?.platform === "web";
  const server = a?.platform === "server";
  const title = !a || web ? "Install, then open your site" : server ? "Send your first event" : "Install, then open your app";
  usePageTitle(title);
  const methods = a ? METHODS[a.platform] : [];
  const m = method && methods.includes(method) ? method : methods[0];
  const host = a?.domains[0];
  const where = event ? event.path || event.name : "";
  const device = event ? [event.browser, event.os].filter(Boolean).join(" on ") || event.device_type || PLATFORM_LABEL[event.platform] || event.platform : "";

  return (
    <OnboardingFrame
      step={setup ? 3 : undefined}
      eyebrow={a ? `${a.name} is ready` : undefined}
      title={title}
      subtitle={
        !a || web
          ? "Pageviews, sources, speed, rage clicks and errors are captured from the start. Most people are done in a couple of minutes."
          : server
            ? "Post events from your backend with the write key. Anything you send shows up here within seconds."
            : "Screens, app opens, rage taps and accessibility settings are captured from the start."
      }
      preview={
        <OnboardingPreview
          step={3}
          eyebrow={event ? "You're live" : "Install"}
          headline={event ? "From here, every release gets a verdict: did it cost you sign-ups or sales, and for whom?" : "Paste one snippet, then watch your first visit arrive."}
          workspace={workspace}
          app={a ? { name: a.name, platform: a.platform } : undefined}
          firstEvent={event ? { where, meta: `${event.browser ?? PLATFORM_LABEL[event.platform] ?? ""} · ${fmtClock(event.ts)}` } : undefined}
        />
      }
    >
      {app.isPending ? (
        <div aria-busy className="space-y-3">
          <Skeleton className="h-40" />
          <Skeleton className="h-24" />
        </div>
      ) : app.isError || !a ? (
        <ErrorState title="Couldn't load this app" error={app.error} />
      ) : (
        <>
          <ol className="space-y-5">
            <InstallStep n={1} title="Copy the code">
              {methods.length > 1 && (
                <TabBar aria-label="Install method" panelId="install-code" items={methods.map((x) => ({ value: x, label: INSTALL_METHOD_LABEL[x] }))} value={m!} onChange={(v) => setMethod(v as InstallMethod)} />
              )}
              <div id="install-code" role={methods.length > 1 ? "tabpanel" : undefined} className="mt-2 min-w-0">
                <CodeBlock
                  code={installSnippets(a, window.location.origin)[m!]}
                  label={INSTALL_METHOD_LABEL[m!]}
                  language={INSTALL_METHOD_LANGUAGE[m!]}
                  onCopy={() => track("install_snippet_copied", { method: m!, welcome: true, setup })}
                />
              </div>
            </InstallStep>
            <InstallStep n={2} title={!web ? (server ? "Call it from your backend" : "Add it to your app") : m === "script" ? "Paste it before </head> on every page" : "Call init() once when your app starts"}>
              <p className="text-[13px] text-ink-2">
                {!web
                  ? server
                    ? "Use the write key in the header. Batches of up to 100 events are fine."
                    : "Where your app starts up, before the first screen shows."
                  : m === "script"
                    ? "In a site builder, look for “custom code” or “header scripts” in the site settings."
                    : "Pageviews in single-page apps are tracked for you."}
              </p>
            </InstallStep>
            <InstallStep n={3} title={web ? "Open your site in a browser" : server ? "Run it once" : "Open your app on a phone or simulator"}>
              <p className="text-[13px] text-ink-2">
                {web ? (
                  host ? (
                    <>
                      Any page on <span className="font-mono text-xs text-ink">{host}</span>. We'll notice the visit here.
                    </>
                  ) : (
                    "Any page with the code on it. We'll notice the visit here."
                  )
                ) : server ? (
                  "We'll notice the event here."
                ) : (
                  "Open a screen or two. We'll notice here."
                )}
              </p>
            </InstallStep>
          </ol>

          <section aria-labelledby="live-title" className={cx("mt-6 rounded-xl border bg-surface p-4", event ? "border-good" : "border-control")}>
            <div className="flex items-center gap-2.5">
              {event ? (
                <CircleCheck size={18} className="shrink-0 text-good" />
              ) : first.isError ? null : (
                <span aria-hidden className="relative m-1 size-2.5 shrink-0 rounded-full bg-accent">
                  <span className="absolute -inset-1 animate-ping rounded-full border-2 border-accent [animation-iteration-count:3] motion-reduce:hidden" />
                </span>
              )}
              <h2 id="live-title" className="text-[15px] font-semibold text-ink">
                {event ? (web ? "Your first visit arrived" : "Your first event arrived") : web ? "Waiting for your first visit…" : "Waiting for your first event…"}
              </h2>
            </div>
            {/* Announces the arrival once; the heading above changes with it. */}
            <p role="status" className="sr-only">
              {event ? `${web ? "Your first visit arrived" : "Your first event arrived"}. Go to ${a.name} is now available.` : ""}
            </p>
            {first.isError ? (
              <FieldError className="mt-2 ml-7">Couldn't check for events. We'll keep trying.</FieldError>
            ) : event ? (
              <>
                <p className="mt-1 ml-7 text-[13px] text-ink-2">Metrickle is working. Every {web ? "visit" : "session"} from now on counts.</p>
                <dl className="mt-3 grid grid-cols-[repeat(auto-fit,minmax(min(8.5rem,100%),1fr))] gap-2">
                  {[
                    [web ? "Page" : "Screen", where],
                    [web ? "Browser" : "Device", device],
                    ["Arrived", `at ${fmtClock(event.ts)}`],
                  ].map(([k, v]) => (
                    <div key={k} className="min-w-0 rounded-lg bg-surface-2 px-2.5 py-2">
                      <dt className="text-[11px] text-ink-3">{k}</dt>
                      <dd className="mt-0.5 truncate text-[13px] font-medium text-ink">{v}</dd>
                    </div>
                  ))}
                </dl>
                <p className="mt-3 text-[13px] text-ink-2">
                  <span className="font-semibold text-ink">Next, to measure sign-ups and sales:</span> add a <code className="rounded bg-surface-2 px-1 font-mono text-xs [overflow-wrap:anywhere]">track("signup")</code> call
                  where people finish signing up, then make it a goal. Your app's overview walks you through it.
                </p>
              </>
            ) : (
              <>
                <p className="mt-1 ml-7 text-[13px] text-ink-2">Checking every few seconds. Leave this page open.</p>
                <Disclosure summary="Nothing showing up?" open={helpOpen} onToggle={setHelpOpen} className="mt-2 ml-7" contentClassName="text-[13px] text-ink-2">
                  <ul className="list-disc space-y-1.5 pl-4">
                    {web ? (
                      <>
                        <li>Check the page with the code has actually loaded: redeploy, then hard refresh.</li>
                        <li>{a.domains.length ? `Open it from one of your allowed addresses: ${a.domains.join(", ")}.` : "Any address is allowed right now, so the address isn't the problem."}</li>
                        <li>Ad and tracking blockers can block the script. Try a private window with extensions off.</li>
                        <li>Bots and headless browsers aren't counted. Test in a normal browser.</li>
                        <li>
                          If your site has a Content-Security-Policy, allow <code className="font-mono text-xs">script-src {window.location.origin}</code> and{" "}
                          <code className="font-mono text-xs">connect-src {window.location.origin.replace("//app.", "//in.")}</code>.
                        </li>
                      </>
                    ) : (
                      <>
                        <li>Check the build with the new code is the one running, and that it starts Metrickle at start-up.</li>
                        <li>Open a couple of screens, then wait a few seconds. Events are sent in batches.</li>
                        <li>Check the host and write key match the code above.</li>
                      </>
                    )}
                  </ul>
                </Disclosure>
              </>
            )}
          </section>

          <div className="mt-5 flex flex-col gap-2">
            {event ? (
              <ButtonLink ref={goRef} variant="primary" to={`/apps/${a.id}`} className="w-full">
                Go to {a.name}
              </ButtonLink>
            ) : (
              <>
                <Button variant="primary" className="w-full" disabled aria-describedby="go-why">
                  Go to {a.name}
                </Button>
                <p id="go-why" className="text-center text-[13px] text-ink-3">
                  Opens when your first {web ? "visit" : "event"} arrives.
                </p>
                <ButtonLink variant="link" to={`/apps/${a.id}`} className="self-center" onClick={() => track("install_skipped", { setup })}>
                  I'll install later
                </ButtonLink>
              </>
            )}
            <p className="mt-1 text-center text-[13px] text-ink-3">
              Using Claude, Cursor or another assistant? <Link to="/connect-ai" className="text-accent-text underline">Connect it</Link> and ask it to install Metrickle for you.
            </p>
          </div>
        </>
      )}
    </OnboardingFrame>
  );
}

function InstallStep({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <li className="min-w-0">
      <p className="flex items-center gap-2.5 text-sm font-semibold text-ink">
        <span aria-hidden className="grid size-[22px] shrink-0 place-items-center rounded-full border border-control bg-surface-2 text-xs text-ink-2">
          {n}
        </span>
        {title}
      </p>
      <div className="mt-2 pl-8">{children}</div>
    </li>
  );
}
